import api from '../config/api';

const blogService = {
  async getPublishedBlogs(page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const response = await api.get('/blog/public', {
        params: { page, size, sortBy, sortDir }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        pagination: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blogs'
      };
    }
  },

  async getBlogBySlug(slug) {
    try {
      const response = await api.get(`/blog/public/${slug}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blog'
      };
    }
  },

  async getBlogsByCategory(category, page = 0, size = 10) {
    try {
      const response = await api.get(`/blog/public/category/${category}`, {
        params: { page, size }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        pagination: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blogs by category'
      };
    }
  },

  async searchBlogs(keyword, page = 0, size = 10) {
    try {
      const response = await api.get('/blog/public/search', {
        params: { keyword, page, size }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        pagination: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to search blogs'
      };
    }
  },

  async getFeaturedBlogs() {
    try {
      const response = await api.get('/blog/public/featured');
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch featured blogs'
      };
    }
  },

  async getLatestBlogs(limit = 5) {
    try {
      const response = await api.get('/blog/public/latest', {
        params: { limit }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch latest blogs'
      };
    }
  },

  async getMostViewedBlogs(limit = 5) {
    try {
      const response = await api.get('/blog/public/most-viewed', {
        params: { limit }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch most viewed blogs'
      };
    }
  },

  async getRelatedBlogs(slug, limit = 3) {
    try {
      const response = await api.get(`/blog/public/related/${slug}`, {
        params: { limit }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch related blogs'
      };
    }
  },

  async getAllCategories() {
    try {
      const response = await api.get('/blog/public/categories');
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch categories'
      };
    }
  },

  // Admin methods
  async getAllBlogs(page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const response = await api.get('/admin/blog/all', {
        params: { page, size, sortBy, sortDir }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        pagination: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch all blogs'
      };
    }
  },

  async getBlogById(id) {
    try {
      const response = await api.get(`/admin/blog/${id}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blog'
      };
    }
  },

  async createBlog(blogData) {
    try {
      const response = await api.post('/admin/blog/create', blogData);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to create blog'
      };
    }
  },

  async updateBlog(id, blogData) {
    try {
      const response = await api.put(`/admin/blog/${id}`, blogData);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update blog'
      };
    }
  },

  async deleteBlog(id) {
    try {
      const response = await api.delete(`/admin/blog/${id}`);
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete blog'
      };
    }
  },

  async getBlogsByStatus(status, page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    try {
      const response = await api.get(`/admin/blog/status/${status}`, {
        params: { page, size, sortBy, sortDir }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        pagination: {
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blogs by status'
      };
    }
  },

  async getBlogStats() {
    try {
      const response = await api.get('/admin/blog/stats');
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch blog stats'
      };
    }
  }
};

export default blogService;
